import { useTranslations } from '@vocab/react';
import {
  Actions,
  Box,
  Button,
  IconStar,
  MenuItem,
  MenuRenderer,
  Strong,
} from 'braid-design-system';
import type { FC } from 'react';

import translations from '../.vocab';
import type { JobApplicationCardProps } from '../JobApplicationCard';

export const StarRatingButton: FC<
  Pick<JobApplicationCardProps, 'onClickRating'> & {
    rating: number;
  }
> = ({ onClickRating, rating }) => {
  const { t } = useTranslations(translations);

  const menuLabel =
    rating === 0 ? (
      <Button size="small" variant="soft" tone="neutral">
        <IconStar />
      </Button>
    ) : (
      <Button
        size="small"
        variant="soft"
        icon={<IconStar active />}
        iconPosition="trailing"
        tone="neutral"
      >
        <Strong>{rating}</Strong>
      </Button>
    );

  const menuItem = Array.from({ length: 5 }, (_, rowIndex) => {
    const starRating = rowIndex + 1;
    return (
      <MenuItem
        key={`${starRating}-rating`}
        aria-label={t('Star rating', { index: starRating })}
        onClick={() => onClickRating(starRating)}
      >
        <Box display="flex" justifyContent="flexStart" gap="xsmall">
          {Array.from({ length: rowIndex + 1 }, (__, index) => (
            <IconStar
              titleId={`IconStar-${starRating}`}
              title={t('Star rating image')}
              active={rating === starRating}
              key={`${starRating} ${index}`}
            />
          ))}
        </Box>
      </MenuItem>
    );
  });

  return (
    <MenuRenderer
      offsetSpace="small"
      trigger={(triggerProps) => (
        <Box {...triggerProps}>
          <Actions>{menuLabel}</Actions>
        </Box>
      )}
    >
      {menuItem}
      {rating !== 0 ? (
        <MenuItem onClick={() => onClickRating(0)}>
          {t('Clear rating')}
        </MenuItem>
      ) : null}
    </MenuRenderer>
  );
};
